import React, { useState, useEffect, useCallback } from "react";
import { Alert, AlertTitle, Box, IconButton, Collapse } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface StatusData {
    active: boolean;
    severity: "info" | "warning" | "error";
    title: string;
    message: string;
    eta?: string;
    updatedAt: string;
}

const STATUS_POLL_INTERVAL_MS = 60000; // Poll every 60 seconds
const STATUS_URL = import.meta.env.VITE_STATUS_URL || "https://d5rywln61l43q.cloudfront.net/status.json";

const StatusBanner: React.FC = () => {
    const [status, setStatus] = useState<StatusData | null>(null);
    const [dismissed, setDismissed] = useState(false);
    const [lastUpdatedAt, setLastUpdatedAt] = useState<string | null>(null);

    const fetchStatus = useCallback(async () => {
        try {
            const response = await fetch(STATUS_URL, {
                method: "GET",
                cache: "no-cache", // Prevent browser caching
                headers: {
                    "Cache-Control": "no-cache, no-store, must-revalidate",
                    Pragma: "no-cache",
                    Expires: "0",
                },
            });

            if (response.ok) {
                const data: StatusData = await response.json();

                // If status changed (different updatedAt), show the banner again
                if (data.updatedAt !== lastUpdatedAt) {
                    setDismissed(false);
                    setLastUpdatedAt(data.updatedAt);
                }

                setStatus(data);
            }
        } catch (error) {
            // Silently fail - status banner is non-critical functionality
            console.debug("Failed to fetch status page data:", error);
        }
    }, [lastUpdatedAt]);

    useEffect(() => {
        // Fetch immediately on mount
        fetchStatus();

        // Poll for updates
        const intervalId = setInterval(fetchStatus, STATUS_POLL_INTERVAL_MS);

        return () => clearInterval(intervalId);
    }, [fetchStatus]);

    const handleDismiss = () => {
        setDismissed(true);
    };

    // Don't render if status is inactive, dismissed, or not loaded yet
    if (!status || !status.active || dismissed) {
        return null;
    }

    // Format ETA if present
    let etaDisplay: string | null = null;
    if (status.eta) {
        try {
            const etaDate = new Date(status.eta);
            const now = new Date();
            const diffMs = etaDate.getTime() - now.getTime();

            if (diffMs > 0) {
                const hours = Math.floor(diffMs / (1000 * 60 * 60));
                const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

                if (hours > 0) {
                    etaDisplay = `Expected resolution in ~${hours}h ${minutes}m`;
                } else if (minutes > 0) {
                    etaDisplay = `Expected resolution in ~${minutes}m`;
                } else {
                    etaDisplay = "Expected resolution shortly";
                }
            }
        } catch {
            // Invalid date format - ignore ETA
        }
    }

    return (
        <Collapse in={!dismissed}>
            <Box
                sx={{
                    position: "fixed",
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 9999,
                    padding: 1,
                }}
            >
                <Alert
                    severity={status.severity}
                    action={
                        <IconButton
                            aria-label="close"
                            color="inherit"
                            size="small"
                            onClick={handleDismiss}
                        >
                            <CloseIcon fontSize="inherit" />
                        </IconButton>
                    }
                    sx={{
                        boxShadow: 3,
                        "& .MuiAlert-message": {
                            width: "100%",
                        },
                    }}
                >
                    {status.title && <AlertTitle>{status.title}</AlertTitle>}
                    {status.message}
                    {etaDisplay && (
                        <Box sx={{ marginTop: 0.5, fontStyle: "italic", fontSize: "0.9em" }}>
                            {etaDisplay}
                        </Box>
                    )}
                </Alert>
            </Box>
        </Collapse>
    );
};

export default StatusBanner;